import {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from "axios";

/**
 * One Server-Sent Event, parsed: its name and its JSON-decoded data.
 */
export interface ServerSentEvent {
    event: string;
    data: unknown;
}

/**
 * What an event handler may return to stop consuming the stream.
 *
 * Returning `false` closes the reader and resolves normally — how a caller
 * ends a stream early without aborting the request, which would surface as a
 * cancellation error instead.
 */
export type ServerSentEventHandler = (event: ServerSentEvent) => boolean | void;

/**
 * Open a Server-Sent Events request through an axios client.
 *
 * The native EventSource cannot carry an Authorization header, so the request
 * rides the client instead — interceptors and JWT included — on a fetch
 * adapter that reads the body as it streams. Resolves when the server closes.
 */
export async function streamServerSentEvents(
    client: AxiosInstance,
    url: string,
    onEvent: ServerSentEventHandler,
    signal?: AbortSignal,
    config: Pick<AxiosRequestConfig, 'params'> = {}
): Promise<void> {
    await client({
        ...config,
        method: 'GET',
        url,
        headers: {Accept: 'text/event-stream'},
        signal,
        adapter: streamingAdapter(onEvent)
    });
}

/**
 * An axios adapter that streams the response and parses it as SSE.
 *
 * A message ends on a blank line; its `event:` line names it and its `data:`
 * lines carry the payload, decoded as JSON when it parses as such and left
 * as raw text otherwise.
 */
const streamingAdapter = (onEvent: ServerSentEventHandler) => {
    return async (config: AxiosRequestConfig): Promise<AxiosResponse> => {
        const response = await fetch(fullUrl(config), {
            method: config.method?.toUpperCase() || 'GET',
            headers: config.headers as HeadersInit,
            signal: config.signal as AbortSignal | undefined
        });

        if (!response.ok) {
            throw await httpError(config, response);
        }

        const reader = response.body?.getReader();

        if (!reader) {
            throw new AxiosError(
                'Response body is not readable',
                AxiosError.ERR_BAD_RESPONSE,
                config as never
            );
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let stopped = false;

        try {
            while (!stopped) {
                const {done, value} = await reader.read();

                if (done) {
                    break;
                }

                // CR and CRLF are both legal SSE terminators; normalising here
                // lets the rest of the parsing assume LF alone.
                buffer += decoder.decode(value, {stream: true}).replace(/\r\n?/g, '\n');

                let boundary = buffer.indexOf('\n\n');

                while (boundary !== -1) {
                    const message = parseMessage(buffer.slice(0, boundary));
                    buffer = buffer.slice(boundary + 2);
                    boundary = buffer.indexOf('\n\n');

                    if (message !== null && onEvent(message) === false) {
                        stopped = true;
                        break;
                    }
                }
            }
        } finally {
            reader.releaseLock();

            // A handler that stopped early leaves the body unread; cancelling
            // releases the connection instead of holding it to the timeout.
            if (stopped) {
                await response.body?.cancel().catch(() => undefined);
            }
        }

        // Nothing reads this: the request is awaited for its stream, and the
        // events went to `onEvent`. It exists to satisfy the adapter contract.
        return {config, status: response.status} as AxiosResponse;
    };
};

/**
 * The absolute url of a streamed request: baseURL joined to the relative url,
 * plus `params` as a query string.
 *
 * axios does this itself for every other adapter; going straight to fetch
 * means doing it here, or `params` would be dropped silently.
 */
const fullUrl = (config: AxiosRequestConfig): string => {
    const url = config.url ?? '';
    const base = config.baseURL ?? '';

    const path = base === '' || /^https?:\/\//i.test(url)
        ? url
        : `${base.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;

    const query = new URLSearchParams(
        (config.params ?? {}) as Record<string, string>
    ).toString();

    return query ? `${path}${path.includes('?') ? '&' : '?'}${query}` : path;
};

/**
 * The error for a non-2xx answer, shaped as an AxiosError so the response
 * interceptor and the status-based handling of callers keep working. The body
 * is read too — losing it would hide a refusal behind a generic failure.
 */
const httpError = async (config: AxiosRequestConfig, response: Response): Promise<AxiosError> => {
    const body = await response.text().catch(() => '');

    let data: unknown = body;

    try {
        data = JSON.parse(body);
    } catch {
        // A non-JSON error body stays the raw text.
    }

    return new AxiosError(
        `Request failed with status code ${response.status}`,
        response.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
        config as never,
        null,
        {
            data,
            status: response.status,
            statusText: response.statusText,
            config
        } as AxiosResponse
    );
};

/**
 * Parse one raw SSE message. A message with no `data:` line — a comment or a
 * keep-alive — parses to null and is skipped.
 */
const parseMessage = (raw: string): ServerSentEvent | null => {
    let event = 'message';
    const data: string[] = [];

    for (const line of raw.split('\n')) {
        if (line.startsWith('event:')) {
            event = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
            data.push(line.slice('data:'.length).trim());
        }
    }

    if (data.length === 0) {
        return null;
    }

    const joined = data.join('\n');

    try {
        return {event, data: JSON.parse(joined)};
    } catch {
        return {event, data: joined};
    }
};
